/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react'
import {
  UnitSelectionContext,
  DatePickerContext,
  SpinContext,
  ErrorContext,
} from 'context'
import { UnitSelection, getApiTargetInfo, ExportComponent } from 'core'
import {
  DatePicker,
  Button,
  Input,
  Empty,
  message,
  Checkbox,
  Tabs,
  Radio,
} from 'antd'
import {
  //  getLevel2ValidationData,
  getLevel2ValidationErrorLog,
  // getLevel2ValidationStatus,
  getLevel2ValidationStatusByPost,
  getLevel2ValidationTab,
  getPjmLevel2ValidationStatus,
} from 'api/pgads.js'

import { connect } from 'react-redux'
import { CriteriaRow, Title, Content, FullHeightSizer } from 'core/screen-util'
import {
  modifierCol,
  modifiedDateCol,
  utilityHeader,
  unitHeader,
} from 'core/HeaderColTypeProps'
import Grid, {
  virtualizedGridRenderer,
  defaultVirtualizedCellRender,
} from '@integec/grid-tools'
import { strCol, intCol, dateCol } from '@integec/grid-tools/lib/cols'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
  isValidYearRange,
} from 'core/utils'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getPermitActions,
  getEnvConfig,
  getValidYearRange,
  getClientInfo,
} from 'core/selectors'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'

import R from 'ramda'
import Task from 'data.task'
import { List } from 'immutable-ext'

import * as ScreenPermissions from 'core/EventActions'
import moment from 'moment'
import { PJM_CLIENT, APS_CLIENT, BROOKFIELD_CLIENT } from 'client'
import { PJMStatusValidationHeaders } from './PjmLevel2ValStatusAntd'

const { TabPane } = Tabs
const validationHeader = [
  intCol({
    ident: 'year',
    display: 'Year',
  }),
  intCol({
    ident: 'month',
    display: 'Month',
  }),
  utilityHeader,
  unitHeader,
  strCol({
    ident: 'shortName',
    display: 'Short Name',
  }),
  strCol({
    ident: 'errorMsg',
    display: 'Error Message',
    width: 850,
    alignment: 'left',
  }),
  dateCol({
    ident: 'errorDate',
    display: 'Error Date',
  }),
  intCol({
    ident: 'errorCode',
    display: 'Error Code',
  }),
  intCol({
    ident: 'severity',
    display: 'Severity',
  }),
  strCol({
    ident: 'deleted',
    display: 'Deleted',
  }),
  modifierCol,
  modifiedDateCol,
].map(e => ({ ...e, isFiltered: true }))
const statusHeader = [
  intCol({
    ident: 'year',
    display: 'Year',
    isKey: true,
  }),
  intCol({
    ident: 'month',
    display: 'Month',
    isKey: true,
  }),
  utilityHeader,
  unitHeader,
  strCol({
    ident: 'status',
    display: 'Status',
    width: 200,
  }),
].map(e => ({ ...e, isFiltered: true }))
const errorHeader = [
  utilityHeader,
  unitHeader,
  intCol({
    ident: 'errorCode',
    display: 'Error Code',
  }),
  strCol({
    ident: 'errorMsg',
    display: 'Error Message',
    width: 450,
    alignment: 'left',
  }),
  modifierCol,
  modifiedDateCol,
].map(e => ({ ...e, isFiltered: true }))

const TabValidationScreen = ({
  apiTargetInfo,
  utilityNameMap,
  unitNameMap,
  actionPermits,
  envConfig,
  CLIENT,
  ...props
}) => {
  const [data, setData] = useState({})
  const [validationData, setValidationData] = useState([])
  const [errorData, setErrorData] = useState([])
  const [statusData, setStatusData] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState('')
  const { selectedUnits, setVisibleUnitSelection } = useContext(
    UnitSelectionContext
  )
  const { selectedDate, setSelectedDate } = useContext(DatePickerContext)
  const [activeKey, setActiveKey] = useState('validation')
  const [showErrorOnly, setShowErrorOnly] = useState(false)
  const [withValidation, setWithValidation] = useState(false)
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [dataType, setDataType] = useState('M')
  const clearData = _ => {
    setData({ errData: [], stsData: [], valData: [] })
    setErrorData([])
    setStatusData([])
    setValidationData([])
  }

  const hideErrorDateValidation = hasAccesPermits({
    actionPermits,
    eventActions: [ScreenPermissions.VALIDATION_HIDE_ERRORDATE],
    matchAny: true,
  })

  const retrieveTask = ({ runCheck = false, selUnits = selectedUnits }) => {
    if (selUnits.length <= 0) {
      message.error('Please,Select Units')
      setVisibleUnitSelection(true)
      return
    }
    setWithValidation(runCheck)
    // setVisibleUnitSelection(false)
    setData({})
    setValidationData([])
    setErrorData([])
    setStatusData([])
    setSpinning(true)
    const reqValidation = []
    if (runCheck) {
      getLevel2ValidationTab(apiTargetInfo)(
        selectedDate.format('YYYY'),
        dataType === 'M' ? selectedDate.format('MM') : '00',
        selUnits[0].utility,
        selUnits.map(u => u.unitId),
        runCheck,
        dataType
      ).fork(setError, md => {
        setSpinning(false)
        setErrorData(md.ERRORLOG)
        setStatusData(md.STATUSLOG)
        setValidationData(md.RUNCHECK || [])
        setData({
          errData: md.ERRORLOG,
          stsData: md.STATUSLOG,
          valData: md.RUNCHECK || [],
        })
        setShowErrorOnly(false)
      })

      setActiveKey('validation')
    } else {
      setActiveKey('status')

      reqValidation.push(
        getLevel2ValidationErrorLog(apiTargetInfo)(
          selectedDate.format('YYYY'),
          dataType === 'M' ? selectedDate.format('MM') : '00',
          selUnits[0].utility, // this screen having single utility selection for
          selUnits.map(u => u.unitId)
        )
      )
      const fromYear = selectedDate.format('YYYY')
      const fromMonth = dataType === 'M' ? selectedDate.format('MM') : '01'
      const toMonth =
        dataType === 'M' ? selectedDate.clone().format('MM') : '12'
      const toYear = selectedDate.clone().format('YYYY')

      const isValid = isValidYearRange(
        props.validYearRange,
        moment(fromYear + '-' + fromMonth + '-01'),
        moment(toYear + '-' + toMonth + '-01')
      )
      if (!isValid) return

      const statusApi =
        CLIENT === PJM_CLIENT || CLIENT === BROOKFIELD_CLIENT
          ? getPjmLevel2ValidationStatus
          : getLevel2ValidationStatusByPost
      reqValidation.push(
        statusApi(apiTargetInfo)(
          fromYear,
          fromMonth,
          toMonth,
          toYear,
          R.uniq(selUnits.map(u => u.utility)),
          selUnits.map(u => u.unitId)
        )
      )
      setSpinning(true)
      List(reqValidation)
        .traverse(Task.of, _ => _)
        .fork(setError, validateData => {
          setSpinning(false)
          let valData = []
          let cur = 0
          if (runCheck) {
            valData = validateData.get(cur)
            cur += 1
          }

          const errData = validateData.get(cur)
          cur += 1
          const stsData = validateData.get(cur)

          setData({ errData, stsData, valData })
          setErrorData(errData)
          setStatusData(stsData)
          setValidationData(valData)
          setShowErrorOnly(false)
        })
    }
  }
  const title = 'Level 2 Validation'
  const headerWithFormatter = useCallback(
    header =>
      header.map(header =>
        header.ident === 'utility'
          ? {
              ...header,
              ...toSelectionColProps(utilityNameMap),
              dataFormatter: ({ value }) => utilityNameMap[value] || value,
              rowHeaderBackgroundColor: '#eee',
              sortIndexGetter: ({ rowData }) =>
                utilityNameMap[rowData.utility] || rowData.utility,
            }
          : header.ident === 'unit'
          ? {
              ...header,
              ...toSelectionColProps(unitNameMap),
              dataFormatter: ({ value }) => unitNameMap[value] || value,
              rowHeaderBackgroundColor: '#eee',
              sortIndexGetter: ({ rowData }) =>
                unitNameMap[rowData.unit] || rowData.unit,
            }
          : { ...header, rowHeaderBackgroundColor: '#eee' }
      ),
    [unitNameMap, utilityNameMap]
  )

  const validationHeaderMemo = useMemo(
    _ => {
      if (CLIENT === APS_CLIENT || hideErrorDateValidation) {
        const nd = validationHeader.filter(f => f.ident !== 'errorDate')
        return headerWithFormatter(nd)
      }
      return headerWithFormatter(validationHeader)
    },
    [headerWithFormatter, CLIENT, hideErrorDateValidation]
  )

  const statusHeaderMemo = useMemo(
    _ => {
      if (CLIENT === PJM_CLIENT || CLIENT === BROOKFIELD_CLIENT)
        return headerWithFormatter(PJMStatusValidationHeaders)
      else return headerWithFormatter(statusHeader)
    },
    [headerWithFormatter, CLIENT]
  )
  const errorHeaderMemo = useMemo(_ => headerWithFormatter(errorHeader), [
    headerWithFormatter,
  ])

  const callback = key => {
    setActiveKey(key)
  }

  const onShowError = e => setShowErrorOnly(e.target.checked)

  useEffect(
    _ => {
      setData({})
      setValidationData([])
      setErrorData([])
      setStatusData([])
    },
    [selectedDate, dataType]
  )

  useEffect(
    _ => {
      // setSpinning(true)
      // setValidationData([])
      /// setErrorData([])
      // setStatusData([])
      setTimeout(_ => {
        if (showErrorOnly) {
          setValidationData(
            (data.valData || []).filter(f => f.severity === '1')
          )
          setErrorData((data.errData || []).filter(f => f.severity === '1'))
          setStatusData(
            (data.stsData || []).filter(f => f.status === 'Not Validated')
          )
        } else {
          setValidationData(data.valData || [])
          setErrorData(data.errData || [])
          setStatusData(data.stsData || [])
        }
      }, 1)

      // setSpinning(false)
    },
    [data.errData, data.stsData, data.valData, setSpinning, showErrorOnly]
  )

  const GridValidation = useRef()
  const GridStatus = useRef()
  const GridError = useRef()

  const exportData = _ => {
    const errExport = new ExportComponent({
      headers: errorHeaderMemo,
      data: data.errData || [],
      fileName: `Level 2 Error-${selectedDate.format('MM-YYYY')}`,
      getData: _ =>
        exportDataComponent(GridError, data.errData || [], fuzzyFilter),
    })

    // if (exportList.includes('Status'))
    const statusExport = new ExportComponent({
      headers: statusHeaderMemo,
      data: data.stsData || [],
      fileName: `Level 2 Status-${selectedDate.format('MM-YYYY')}`,
      getData: _ =>
        exportDataComponent(GridStatus, data.stsData || [], fuzzyFilter),
    })
    if (withValidation) {
      const valExport = new ExportComponent({
        headers: validationHeaderMemo,
        data: data.valData || [],
        fileName: `Level 2 Validation-${selectedDate.format('MM-YYYY')}`,
        getData: _ =>
          exportDataComponent(GridValidation, data.valData || [], fuzzyFilter),
      })
      valExport.exportCSVData()
    }

    errExport.exportCSVData()
    statusExport.exportCSVData()
  }
  // render is not rendering fi use memo header in grid directly
  return (
    <div
      style={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <UnitSelection
        setData={() => setData({})}
        retrieveData={({ selUnits }) =>
          retrieveTask({ runCheck: false, selUnits })
        }
      />
      <CriteriaRow>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
          }}
        >
          {title}
        </Title>

        <DatePicker.MonthPicker
          allowClear={false}
          format="MMMM-YYYY"
          value={selectedDate}
          onChange={d => {
            setSelectedDate(d.endOf('month'))
            clearData()
          }}
        />
        <Radio.Group
          onChange={e => {
            setDataType(e.target.value)
            clearData()
          }}
          value={dataType}
          style={{ marginLeft: '10px' }}
        >
          <Radio value="M" style={{ marginLeft: '10px' }}>
            Monthly
          </Radio>
          <Radio value="Y">Yearly-{selectedDate.format('YYYY')}</Radio>
        </Radio.Group>
        <Button
          loading={spinning}
          onClick={() => retrieveTask({ runCheck: false })}
          style={{ marginLeft: '10px' }}
          type="primary"
          // icon="pushpin"
          icon="database"
          spinning={spinning}
        >
          Retrieve
        </Button>

        {hasAccesPermits({
          authGodMode: envConfig.authGodMode,
          actionPermits,
          eventActions: [ScreenPermissions.VALIDATION_RUNCHECK],
          matchAny: true,
        }) && (
          <Button
            loading={spinning}
            onClick={() => retrieveTask({ runCheck: true })}
            style={{ marginLeft: '25px' }}
            type="danger"
            icon="thunderbolt"
            spinning={spinning}
          >
            Run Check
          </Button>
        )}

        <div css={{ flex: '1 1 0px' }} />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          css={{ marginLeft: '10px', marginRight: '10px', flex: '0 0 200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <Button
          onClick={exportData}
          icon="export"
          disabled={
            validationData.length < 1 &&
            errorData.length < 1 &&
            statusData.length < 1
          }
        >
          Export
        </Button>
      </CriteriaRow>

      <FullHeightSizer heightOffset={250}>
        {({ height, width }) =>
          validationData.length > 0 ||
          errorData.length > 0 ||
          statusData.length > 0 ? (
            <Content style={{ height: height - 20, paddingTop: '0px' }}>
              <Tabs
                defaultActiveKey={withValidation ? 'validation' : 'error'}
                onChange={callback}
                style={{ height: 'inherit', paddingTop: '0px' }}
                activeKey={activeKey}
                tabBarExtraContent={
                  <Checkbox
                    checked={showErrorOnly}
                    style={{ marginRight: '500px' }}
                    onChange={onShowError}
                  >
                    Show Error Only
                  </Checkbox>
                }
              >
                {withValidation && (
                  <TabPane tab="Validation" key="validation" forceRender>
                    <Grid
                      ref={GridValidation}
                      sortEnabled
                      isEditable={false}
                      data={validationData}
                      headers={headerWithFormatter(validationHeaderMemo)}
                      altBgColor="#ebebeb"
                      render={virtualizedGridRenderer({
                        autoFixColByKey: true,
                        rowHeaderRender,
                        height: height - 100,
                        width: width - 100,
                        headerRowHeight: 30,
                        cellRender,
                      })}
                      fuzzyFilter={fuzzyFilter}
                      initialSortOptions={[utilityHeader, unitHeader]}
                    />
                  </TabPane>
                )}
                <TabPane tab="Error" key="error" forceRender>
                  {errorData.length > 0 && (
                    <Grid
                      ref={GridError}
                      sortEnabled
                      isEditable={false}
                      data={errorData}
                      headers={headerWithFormatter(errorHeader)}
                      altBgColor="#ebebeb"
                      render={virtualizedGridRenderer({
                        autoFixColByKey: false,
                        rowHeaderRender,
                        height: height - 100,
                        width: width - 70,
                        headerRowHeight: 30,
                        cellRender,
                      })}
                      fuzzyFilter={fuzzyFilter}
                      initialSortOptions={[utilityHeader, unitHeader]}
                    />
                  )}
                  {errorData.length === 0 && (
                    <div> Processed Successfully. </div>
                  )}
                </TabPane>
                <TabPane tab="Status" key="status" forceRender>
                  <Grid
                    ref={GridStatus}
                    sortEnabled
                    isEditable={false}
                    data={statusData}
                    headers={statusHeaderMemo}
                    altBgColor="#ebebeb"
                    render={virtualizedGridRenderer({
                      autoFixColByKey: false,
                      rowHeaderRender,
                      height: height - 100,
                      headerRowHeight: 30,
                      width: width - 50,
                      cellRender: statusRender,
                    })}
                    fuzzyFilter={fuzzyFilter}
                    initialSortOptions={[
                      utilityHeader,
                      unitHeader,
                      {
                        ident: 'year',
                        sortOrder: 'asc',
                      },
                      {
                        ident: 'month',
                        display: 'Month',
                        sortOrder: 'asc',
                      },
                    ]}
                  />
                </TabPane>
              </Tabs>
            </Content>
          ) : (
            <Content style={{ height: '200px' }}>
              <Empty
                imageStyle={{ paddingTop: '30px' }}
                description={
                  selectedUnits && selectedUnits.length <= 0 ? (
                    'Please select units'
                  ) : (
                    <div>
                      Pressing the <b>Retrieve</b> button will only show the
                      Validation Status and Error log (if any) from prior
                      validations. Press the <b>Run Check</b> button to perform
                      Validation.
                    </div>
                  )
                }
              />
            </Content>
          )
        }
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
  validYearRange: getValidYearRange(state),
  CLIENT: getClientInfo(state),
})

export const cellRender = props => {
  const { gridToolProps, reactVirtualizedProps } = props
  const { data } = gridToolProps
  const { style, rowIndex } = reactVirtualizedProps
  const { severity } = data[rowIndex]

  // const style = reactVirtualizedProps.style
  return defaultVirtualizedCellRender({
    ...props,
    hoverSelectionColor: '#000000',
    selectionColor: '#000000',
    selectionBackgroundColor: 'white',
    hoverSelectionBackgroundColor: 'white',
    // hoverColor: '#00000',
    // color: '#00000',
    style: {
      ...style,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      borderTop: '1px solid #cc',
      //       backgroundColor: severity === '1' ? '#f7bbbb' : '#FFFF66',
      backgroundColor: severity === '1' ? ' #fad1d1' : '#ffff99',
      color: '#1d2025',
    },
  })
}

export const statusRender = props => {
  const { gridToolProps, reactVirtualizedProps } = props
  const { headers, data } = gridToolProps
  const { columnIndex, style, rowIndex } = reactVirtualizedProps
  const { status } = data[rowIndex]
  if (headers[columnIndex].ident === 'status')
    return defaultVirtualizedCellRender({
      ...props,

      style: {
        ...style,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        borderTop: '1px solid #cc',
        // backgroundColor: status === 'Not Validated' ? '#f7bbbb' : '#FFFFFF',
        // color: '#00000',
        backgroundColor: status === 'Not Validated' ? '#f7bbbb' : '#FFFFFF',
        color: '#1d2025',
      },
    })

  return defaultVirtualizedCellRender({
    ...props,
    hoverSelectionColor: '#00000',
    selectionColor: '#00000',
    hoverColor: '#00000',
    color: '#00000',
    style: {
      ...style,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      borderTop: '1px solid #cc',
    },
  })
}

export default connect(mapStateToProps)(TabValidationScreen)
